import { animated, config, useTransition } from '@react-spring/web';
import { memo, type RefObject, useCallback, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type PDFPagesCache } from '../../utils/PDFPagesCache';
import {
  PDFViewerOutlinePage,
  type RenderOutlinePageMenu,
} from './components/PDFViewerOutlinePage/PDFViewerOutlinePage';
import {
  PDFViewerOutlineVirtualizedList,
  type PDFViewerOutlineVirtualizedListHandle,
  type PDFViewerOutlineVirtualizedListProps,
} from './components/PDFViewerOutlineVirtualizedList';
import { PDFViewerOutlineImagesCache } from './utils/PDFViewerOutlineImagesCache';

type PDFViewerOutlineProps = {
  isOpen: boolean;
  currentPage: number;
  onPageClick: (pageNumber: number) => void;
  pdfPageCacheRef: RefObject<PDFPagesCache | undefined>;
  pagesCount: number;
  renderOutlinePageMenu?: RenderOutlinePageMenu;
};

export const PDF_OUTLINE_WIDTH = 192;

export const PDFViewerOutline = memo(
  ({ isOpen, pdfPageCacheRef, pagesCount, currentPage, onPageClick, renderOutlinePageMenu }: PDFViewerOutlineProps) => {
    const listRef = useRef<PDFViewerOutlineVirtualizedListHandle>(null);
    const pdfOutlineThumbnailsCacheRef = useRef<PDFViewerOutlineImagesCache | null>();

    useIsomorphicLayoutEffect(() => {
      pdfOutlineThumbnailsCacheRef.current = new PDFViewerOutlineImagesCache();

      return () => {
        pdfOutlineThumbnailsCacheRef.current?.cleanup();
        pdfOutlineThumbnailsCacheRef.current = null;
      };
    }, []);

    const transitions = useTransition(isOpen, {
      config: config.stiff,
      enter: { opacity: 1, x: 0 },
      from: { opacity: 0, x: -PDF_OUTLINE_WIDTH },
      leave: { opacity: 0, x: -PDF_OUTLINE_WIDTH },
    });

    useEffect(() => {
      listRef.current?.scrollToIndex(currentPage - 1);
    }, [currentPage]);

    const renderOutlinePages = useCallback<PDFViewerOutlineVirtualizedListProps['children']>(
      ({ index }) => {
        const pageNumber = index + 1;

        return (
          <PDFViewerOutlinePage
            isSelected={currentPage === pageNumber}
            onPageClick={onPageClick}
            pageNumber={pageNumber}
            pdfPageCacheRef={pdfPageCacheRef}
            renderOutlinePageMenu={renderOutlinePageMenu}
            thumbnailCacheRef={pdfOutlineThumbnailsCacheRef}
          />
        );
      },
      [currentPage, pdfPageCacheRef, onPageClick, renderOutlinePageMenu],
    );

    return transitions(
      (styles, item) =>
        item && (
          <animated.div
            className="absolute top-0 left-0 flex h-full w-48 items-start justify-end bg-gray-12"
            style={styles}
          >
            <div
              className="flex size-full bg-gray-1"
              style={{
                boxShadow: '1px 0px 0px var(--colors-gray-3)',
              }}
            >
              <PDFViewerOutlineVirtualizedList ref={listRef} rowsCount={pagesCount}>
                {renderOutlinePages}
              </PDFViewerOutlineVirtualizedList>
            </div>
          </animated.div>
        ),
    );
  },
);

PDFViewerOutline.displayName = 'PDFViewerOutline';
